// Copyright ©️ 2026 eVolve MEP, LLC

import type { AutodeskUrn } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import type { SpoolingSettings } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';
import { useSpoolRequestsViewerState } from 'modules/Shop/Spooling/hooks/useSpoolRequestsViewerState';

import { RequestNewSpools } from './RequestNewSpools';

type RequestingNewSpoolsHeaderBarProps = {
  urn: AutodeskUrn;
  requestingNewSpools: boolean;
  setRequestingNewSpools: (isRequesting: boolean) => void;
  spoolingSettings: SpoolingSettings;
};

export const RequestingNewSpoolsHeaderBar = ({
  urn,
  requestingNewSpools,
  spoolingSettings,
  setRequestingNewSpools,
}: RequestingNewSpoolsHeaderBarProps) => {
  const spools = useAutodeskExtensionStoreSnapshot('spools');
  const { spoolRequests } = useSpoolRequestsViewerState();
  useBlockerWhen('requesting-spools', requestingNewSpools);

  return requestingNewSpools && isNotNil(spools) && isNotNil(spoolRequests) ? (
    <RequestNewSpools
      spoolingSettings={spoolingSettings}
      key={urn}
      onClose={() => {
        setRequestingNewSpools(false);
      }}
      urn={urn}
    />
  ) : (
    <Button
      variant="outline"
      onClick={() => {
        setRequestingNewSpools(true);
      }}
      leftSection={<EvolveIcon icon="Add" />}
      disabled={isNil(spools) || isNil(spoolRequests)}
    >
      Request new spool
    </Button>
  );
};
